import { Add, Edit, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  DialogContentText,
  DialogActions,
  Stack,
  IconButton,
  Dialog as MuiDialog,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  deDE,
} from "@mui/x-data-grid";
import React, { useState } from "react";

import {
  useGetLegalClientGroups,
  useCreateLegalClientGroup,
  useUpdateLegalClientGroup,
  useDeleteLegalClientGroup,
} from "../hooks/reactQuerys/LegalClientGroups";

import {
  ILegalClientGroup,
  createEmptyLegalClientGroup,
} from "../Interfaces/ILegalClientGroup";
import { LegalClientGroupEdit } from './LegalClientGroupEdit';

export const LegalClientGroupOverview: React.FC = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const [editGroup, setEditGroup] =
    useState<ILegalClientGroup>(createEmptyLegalClientGroup());

  const { legalClientGroups, isLoadingLegalClientGroups, refetchLegalClientGroups } =
    useGetLegalClientGroups();

  const { createLegalClientGroup, isPendingCreateLegalClientGroup } =
    useCreateLegalClientGroup();

  const { updateLegalClientGroup, isPendingUpdateLegalClientGroup } =
    useUpdateLegalClientGroup();

  const { deleteLegalClientGroup, isPendingDeleteLegalClientGroup } =
    useDeleteLegalClientGroup();

  const isSaving =
    isPendingCreateLegalClientGroup ||
    isPendingUpdateLegalClientGroup ||
    isPendingDeleteLegalClientGroup;

  const handleOpen = (group?: ILegalClientGroup) => {
    setEditGroup(group ?? createEmptyLegalClientGroup());
    setIsEditOpen(true);
  };

  const handleClose = () => {
    setEditGroup(createEmptyLegalClientGroup());
    setIsEditOpen(false);
  };

  const handleSave = async (): Promise<ILegalClientGroup> => {
    if (!editGroup.Name.trim()) throw new Error('Name required');

    let saved: ILegalClientGroup;

    if (editGroup.idLegalClientGroup < 0) {
      saved = await createLegalClientGroup({
        Name: editGroup.Name.trim(),
        ColorHex: editGroup.ColorHex,
        Description: editGroup.Description ?? null,
      });
    } else {
      saved = await updateLegalClientGroup({
        ...editGroup,
        Description: editGroup.Description ?? null,
      });
    }

    // wichtig: lokal auch aktualisieren, damit Dialog ggf. die neue ID sieht
    setEditGroup(saved);

    await refetchLegalClientGroups();

    return saved;
  };


  const handleDelete = async () => {
    if (editGroup.idLegalClientGroup < 0) return;

    await deleteLegalClientGroup(editGroup.idLegalClientGroup);
    await refetchLegalClientGroups();

    setIsDeleteConfirmOpen(false);
    handleClose();
  };

  const groupColumns: GridColDef[] = [
    { field: 'idLegalClientGroup', headerName: 'Nr.', width: 90 },
    { field: 'Name', headerName: 'Name', flex: 1 },

    {
      field: 'Description',
      headerName: 'Beschreibung',
      flex: 2,
      sortable: false,
      renderCell: (params) =>
        params.value ? params.value : "-",
    },

    {
      field: 'ColorHex',
      headerName: 'Farbe',
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.ColorHex}
          sx={{
            width: 90,
            justifyContent: 'center',
            backgroundColor: params.row.ColorHex,
            color: '#fff',
          }}
        />
      ),
    },


    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 120,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpen(params.row);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditGroup(params.row);
              setIsDeleteConfirmOpen(true);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];


  return (
    <>
      <LegalClientGroupEdit
        open={isEditOpen}
        group={editGroup}
        isSaving={isSaving}
        onChange={g => setEditGroup(g)}
        onClose={handleClose}
        onSave={handleSave}
        onDeleteRequest={() => setIsDeleteConfirmOpen(true)}
      />

      {/* Delete Confirm */}
      <MuiDialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
      >
        <Typography variant='h6' sx={{ p: 2 }}>
          Möchten Sie diese Gruppe wirklich löschen?
        </Typography>
        <DialogContentText sx={{ px: 2 }}>
          Diese Aktion kann nicht rückgängig gemacht werden.
        </DialogContentText>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>
            Abbrechen
          </Button>
          <Button color='error' onClick={handleDelete} disabled={isSaving}>
            Löschen
          </Button>
        </DialogActions>
      </MuiDialog>

      {/* Tabelle */}
      <Box sx={{ m: 2 }}>
        <Typography variant='h4' sx={{ mb: 2 }}>
          Mandantengruppen
          <IconButton
            sx={{ float: 'right' }}
            onClick={() => handleOpen(undefined)}
          >
            <Add />
          </IconButton>
        </Typography>

        <DataGrid
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={legalClientGroups}
          columns={groupColumns}
          getRowId={row => row.idLegalClientGroup}
          autoHeight
          loading={isLoadingLegalClientGroups}
          onRowClick={params => handleOpen(params.row)}
        />
      </Box>
    </>
  );
};
